/**
 * Layers panel (_docs/tilemap/05 §8). Lists layers top-to-bottom (so the row
 * order matches visual stacking, top row = front), with per-row visibility,
 * lock, active-layer selection, and reorder up/down. Add creates a layer from a
 * chosen tileset; a row menu duplicates/deletes. The active layer is the paint
 * target and drives which tileset the palette shows.
 */

import { useState } from 'react';
import { useTilemapStore, tilemapStore, orderedLayers } from '../store/tilemapStore';
import { tilemapClient } from '../api/tilemapClient';

export function LayersPanel({ projectUuid }: { projectUuid: string }) {
    const doc = useTilemapStore((s) => s.doc);
    const activeLayerUuid = useTilemapStore((s) => s.activeLayerUuid);
    const [adding, setAdding] = useState(false);

    if (!doc) return null;
    const layers = orderedLayers(doc); // bottom-to-top
    const topToBottom = [...layers].reverse();

    const setActive = (uuid: string) => tilemapStore.getState().setActiveLayer(uuid);

    const move = async (uuid: string, dir: -1 | 1) => {
        // Reorder within the bottom-to-top array.
        const order = layers.map((l) => l.uuid);
        const i = order.indexOf(uuid);
        const j = i + dir;
        if (j < 0 || j >= order.length) return;
        [order[i], order[j]] = [order[j], order[i]];
        await tilemapClient.reorderLayers(projectUuid, doc.tilemap.uuid, order);
        await tilemapStore.getState().reload();
    };

    const toggle = async (uuid: string, field: 'visible' | 'locked', value: boolean) => {
        await tilemapClient.updateLayer(uuid, { [field]: value });
        await tilemapStore.getState().reload();
    };

    const rename = async (uuid: string, name: string) => {
        await tilemapClient.updateLayer(uuid, { name });
        await tilemapStore.getState().reload();
    };

    const setOpacity = async (uuid: string, opacity: number) => {
        await tilemapClient.updateLayer(uuid, { opacity });
        await tilemapStore.getState().reload();
    };

    const remove = async (uuid: string, name: string, tiles: number) => {
        if (!confirm(`Delete layer "${name}"${tiles ? ` and its ${tiles} painted tiles` : ''}?`)) return;
        await tilemapClient.deleteLayer(uuid);
        await tilemapStore.getState().reload();
    };

    const mergeDown = async (uuid: string, name: string) => {
        if (!confirm(`Merge "${name}" down into the layer beneath it? This deletes "${name}".`)) return;
        const result = await tilemapStore.getState().mergeLayerDown(uuid);
        if (!result.ok) alert(result.error);
    };

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex align-items-center gap-2 p-2 border-bottom">
                <span className="fw-bold small">Layers</span>
                <button className="btn btn-sm btn-outline-primary ms-auto" onClick={() => setAdding(true)}>
                    <i className="ti ti-plus" /> Add
                </button>
            </div>

            <div className="overflow-auto flex-fill">
                {topToBottom.length === 0 && (
                    <div className="p-3 text-secondary small">No layers yet. Add one from a tileset.</div>
                )}
                {topToBottom.map((layer) => {
                    const painted = layer.chunk_index.reduce((n, c) => n + c.used_count, 0);
                    const isActive = layer.uuid === activeLayerUuid;
                    return (
                        <div
                            key={layer.uuid}
                            className={`px-2 py-1 border-bottom ${isActive ? 'bg-primary-lt' : ''}`}
                            onClick={() => setActive(layer.uuid)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center gap-1">
                                <button className="btn btn-sm btn-ghost-secondary p-0 px-1" title={layer.visible ? 'Hide' : 'Show'}
                                    onClick={(e) => { e.stopPropagation(); void toggle(layer.uuid, 'visible', !layer.visible); }}>
                                    <i className={`ti ti-${layer.visible ? 'eye' : 'eye-off'}`} />
                                </button>
                                <button className="btn btn-sm btn-ghost-secondary p-0 px-1" title={layer.locked ? 'Unlock' : 'Lock'}
                                    onClick={(e) => { e.stopPropagation(); void toggle(layer.uuid, 'locked', !layer.locked); }}>
                                    <i className={`ti ti-${layer.locked ? 'lock' : 'lock-open'}`} />
                                </button>
                                <input
                                    className="form-control form-control-sm border-0 bg-transparent flex-fill px-1"
                                    defaultValue={layer.name}
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={(e) => { if (e.target.value !== layer.name) void rename(layer.uuid, e.target.value); }}
                                />
                                <button className="btn btn-sm btn-ghost-secondary p-0 px-1" title="Move up"
                                    onClick={(e) => { e.stopPropagation(); void move(layer.uuid, 1); }}>
                                    <i className="ti ti-chevron-up" />
                                </button>
                                <button className="btn btn-sm btn-ghost-secondary p-0 px-1" title="Move down"
                                    onClick={(e) => { e.stopPropagation(); void move(layer.uuid, -1); }}>
                                    <i className="ti ti-chevron-down" />
                                </button>
                                <button className="btn btn-sm btn-ghost-danger p-0 px-1" title="Delete"
                                    onClick={(e) => { e.stopPropagation(); void remove(layer.uuid, layer.name, painted); }}>
                                    <i className="ti ti-trash" />
                                </button>
                            </div>
                            {layers.findIndex((l) => l.uuid === layer.uuid) > 0 && (
                                <div className="ps-4 pe-1 pb-1">
                                    <button className="btn btn-sm btn-outline-secondary py-0" title="Merge this layer's tiles down into the layer beneath it (requires the same tileset)"
                                        onClick={(e) => { e.stopPropagation(); void mergeDown(layer.uuid, layer.name); }}>
                                        <i className="ti ti-arrow-bar-to-down me-1" /> Merge down
                                    </button>
                                </div>
                            )}
                            <div className="d-flex align-items-center gap-2 ps-4 pe-1">
                                <input type="range" min={0} max={1} step={0.05} value={layer.opacity} className="form-range"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => void setOpacity(layer.uuid, Number(e.target.value))} />
                                <span className="text-secondary small" style={{ width: 60 }}>{painted} tiles</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {adding && (
                <AddLayerForm
                    projectUuid={projectUuid}
                    onClose={() => setAdding(false)}
                    onAdded={async () => { setAdding(false); await tilemapStore.getState().reload(); }}
                />
            )}
        </div>
    );
}

function AddLayerForm({ projectUuid, onClose, onAdded }: { projectUuid: string; onClose: () => void; onAdded: () => void }) {
    const doc = useTilemapStore((s) => s.doc)!;
    const [name, setName] = useState(`Layer ${doc.layers.length + 1}`);
    const [tilesetUuid, setTilesetUuid] = useState(doc.tilesets[0]?.uuid ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const add = async () => {
        if (!tilesetUuid) { setError('This map has no tilesets. Create one in the Tile Palette first.'); return; }
        setBusy(true);
        try {
            await tilemapClient.createLayer(projectUuid, doc.tilemap.uuid, { name: name.trim() || 'Layer', tileset_uuid: tilesetUuid });
            onAdded();
        } catch (e) {
            setError((e as Error).message);
            setBusy(false);
        }
    };

    return (
        <div className="modal modal-blur show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header py-2"><h6 className="modal-title mb-0">Add layer</h6><button className="btn-close" onClick={onClose} /></div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger py-1 small">{error}</div>}
                        <input className="form-control form-control-sm mb-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Layer name" />
                        <select className="form-select form-select-sm" value={tilesetUuid} onChange={(e) => setTilesetUuid(e.target.value)}>
                            {doc.tilesets.length === 0 && <option value="">No tilesets</option>}
                            {doc.tilesets.map((t) => <option key={t.uuid} value={t.uuid}>{t.name}</option>)}
                        </select>
                    </div>
                    <div className="modal-footer py-1">
                        <button className="btn btn-sm btn-link" onClick={onClose}>Cancel</button>
                        <button className="btn btn-sm btn-primary" disabled={busy} onClick={add}>Add</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
